import { QUESTS, type QuestDefinition, type QuestId, type QuestStage } from '@/data/quests';
import type { Friendships } from '@/world/friendship';
import type { Inventory } from '@/world/inventory';
import type { Wallet } from '@/world/wallet';

/**
 * Where each quest has got to.
 *
 * Saved, because a chain is a history rather than a state of the world: the
 * materials it wanted are long spent, and nothing left standing on the map
 * could be read to work out how far through it you are.
 *
 * A quest is a stage index and nothing else. Progress within a stage is the
 * contents of the backpack, which is already saved — counting deliveries
 * separately would be a second source of truth for the same fact.
 */

export type QuestState = 'unavailable' | 'offered' | 'active' | 'complete';

export interface QuestProgress {
  readonly quest: QuestDefinition;
  readonly state: QuestState;
  /** The stage now being asked for, or null when there is nothing left. */
  readonly stage: QuestStage | null;
  readonly stageIndex: number;
  /** How much of the current stage's item is to hand. */
  readonly carrying: number;
  readonly canHandIn: boolean;
}

export interface HandInContext {
  readonly inventory: Inventory;
  readonly wallet: Wallet;
  readonly friendships: Friendships;
}

export interface HandInOutcome {
  readonly line: string;
  /** True on the hand-in that finished the whole chain. */
  readonly finished: boolean;
  /** The recipe the chain unlocked, on the hand-in that finished it. */
  readonly unlocked: string | null;
}

export class QuestLog {
  /** Quest id to the index of the stage now wanted. Absent means not started. */
  private readonly started = new Map<QuestId, number>();

  /** Whether a quest has been taken up at all. */
  isStarted(id: QuestId): boolean {
    return this.started.has(id);
  }

  isComplete(id: QuestId): boolean {
    const stage = this.started.get(id);
    return stage !== undefined && stage >= QUESTS[id].stages.length;
  }

  /** Recipes unlocked by finished quests. Derived, so it cannot drift. */
  unlockedRecipes(): Set<string> {
    const unlocked = new Set<string>();
    for (const id of this.started.keys()) {
      if (this.isComplete(id)) unlocked.add(QUESTS[id].reward.recipe);
    }
    return unlocked;
  }

  /**
   * Everything about a quest right now.
   *
   * One call rather than a handful of predicates, because every caller — the
   * dialogue, a test, whatever shows a log later — wants the same four facts
   * together and would otherwise ask for them one at a time.
   */
  progress(id: QuestId, context: { inventory: Inventory; friendships: Friendships }): QuestProgress {
    const quest = QUESTS[id];
    const stageIndex = this.started.get(id) ?? 0;
    const complete = this.isComplete(id);
    const stage = complete ? null : quest.stages[stageIndex] ?? null;

    const state: QuestState = complete
      ? 'complete'
      : this.started.has(id)
        ? 'active'
        : context.friendships.pointsFor(quest.giver) >= quest.requiresPoints
          ? 'offered'
          : 'unavailable';

    const carrying = stage ? context.inventory.countOf(stage.item) : 0;

    return {
      quest,
      state,
      stage,
      stageIndex,
      carrying,
      // Only an active quest can be handed in: taking the materials off
      // someone who has not agreed to the job would be theft with a story.
      canHandIn: state === 'active' && stage !== null && carrying >= stage.count,
    };
  }

  /** Take the job. */
  start(id: QuestId): void {
    if (!this.started.has(id)) this.started.set(id, 0);
  }

  /**
   * Hand over the current stage.
   *
   * Takes nothing unless the whole stage can be met, for the same reason the
   * notice board does not: a partial delivery that keeps the goods and does
   * not advance is the worst thing that could happen here.
   */
  handIn(id: QuestId, context: HandInContext): HandInOutcome | null {
    const state = this.progress(id, context);
    if (!state.canHandIn || !state.stage) return null;

    context.inventory.remove(state.stage.item, state.stage.count);
    this.started.set(id, state.stageIndex + 1);

    if (!this.isComplete(id)) {
      return { line: state.stage.done, finished: false, unlocked: null };
    }

    const { reward } = state.quest;
    context.wallet.earn(reward.coins);
    context.friendships.add(state.quest.giver, reward.friendship);

    return { line: state.stage.done, finished: true, unlocked: reward.recipe };
  }

  toSave(): Record<string, number> {
    return Object.fromEntries(this.started);
  }

  static fromSave(value: unknown): QuestLog {
    const log = new QuestLog();
    if (!value || typeof value !== 'object') return log;

    for (const [id, stage] of Object.entries(value as Record<string, unknown>)) {
      if (!(id in QUESTS)) continue;

      const index = Math.floor(Number(stage));
      if (!Number.isFinite(index) || index < 0) continue;

      // Clamped to one past the last stage: a save naming a stage that no
      // longer exists reads as finished rather than as a quest stuck asking
      // for something nobody wrote.
      log.started.set(id as QuestId, Math.min(index, QUESTS[id as QuestId].stages.length));
    }

    return log;
  }
}
